import PropTypes from 'prop-types'
import { PatternFormat } from 'react-number-format'
import { TextField, InputAdornment } from '@mui/material'
import { useFieldState } from 'hooks/useFieldState'
import { Check, ErrorOutline } from '@mui/icons-material'
import { useRef, useEffect, useState } from 'react'
import { useFieldFocus } from 'context/FieldFocusContext'

const MuiPhoneNumberField = ({
  id,
  value,
  label,
  placeholder,
  onChange,
  onFocus,
  onBlur = () => {},
  disabled,
  showAdornment = false,
  enableHelpText,
  helpText,

  required,
  defaultErrorState,
  forceError,
  ...rest
}) => {
  //this checks to see if its a 10 digit number, and the field doesnt allow anything except for numbers
  //eventually we will need country codes when our auth supports them
  const [rawValue, setRawValue] = useState(value)
  const validatePhoneNumber = (val) => {
    if (!val) return
    const digitsOnly = val.replace(/\D/g, '')
    const normalized =
      digitsOnly.length === 11 && digitsOnly.startsWith('1')
        ? digitsOnly.slice(1)
        : digitsOnly
    return /^\d{10}$/.test(normalized)
  }

  const { isError, isSuccess, handleOnblur } = useFieldState({
    required,
    defaultErrorState,
    validatorFunction: validatePhoneNumber,
    forceError,
  })

  const handleOnblurWithValidation = (e) => {
    const isValid = validatePhoneNumber(value)
    handleOnblur?.(value)
    handleBlur()
    if (isValid) {
      onBlur?.(value)
    }
  }

  const { handleFocus, handleBlur, registerHelpField } = useFieldFocus()
  const inputRef = useRef(null)
  const fieldId = id || name

  useEffect(() => {
    if (enableHelpText && fieldId) {
      registerHelpField(fieldId, helpText)
    }
  }, [enableHelpText, fieldId])

  const styles = {
    '& .MuiInputLabel-asterisk': {
      color: '#ea0606',
    },
    '&>label': {
      fontSize: '14px',
      color: '#75787B',
      top: '-2px',
    },
    '&>label.Mui-focused': {
      fontSize: '1rem',
      color: '#2370ED',
      background: '#fff',
      padding: '0 5px',
      marginLeft: '-5px',
      top: 0,
    },
    '&>label.Mui-focused.Mui-error': {
      color: '#ea0606',
    },
    '&>label.MuiFormLabel-filled, &>label.MuiInputLabel-shrink': {
      fontSize: '1rem',
      background: '#fff',
      padding: '0 5px',
      marginLeft: '-5px',
      top: 0,
    },
    '&>label.MuiFormLabel-filled.Mui-disabled': {
      top: 0,
      background:
        'linear-gradient(to bottom, #fff 0%, #fff 50%, #e9ecef 50%, #e9ecef 100%)',
    },
    '&>div.MuiInputBase-root': {
      padding: '0!important',
      borderRadius: '4px !important',
    },
    '&>div.MuiInputBase-root.Mui-disabled': {
      backgroundColor: '#e9ecef!important',
    },
    '&>div.MuiInputBase-root.Mui-focused': {
      boxShadow: '0 0 0 1.5px #2370ED',
    },
    '&>div>.MuiInputBase-input': {
      width: '100%!important',
      padding: '0.75rem 1.75rem 0.75rem 0.75rem!important',
      display: 'flex',
    },
    '&>div.MuiInputBase-root>svg': {
      display: 'block',
      height: '1.5em',
      width: '1.5em',
      top: 'calc(50% - 0.75em)',
    },
  }

  return (
    <PatternFormat
      disabled={disabled}
      required={required}
      format="+1 (###) ###-####"
      mask="_"
      value={value}
      // onValueChange={(values) => onChange(values.value)}
      onValueChange={({ value }) => {
        const digitsOnly =
          value.length === 11 && value.startsWith('1') ? value.slice(1) : value
        onChange?.(digitsOnly)
      }}
      // onChange={onChange}
      customInput={TextField}
      label={label}
      placeholder={placeholder}
      error={isError}
      id={fieldId}
      name={fieldId}
      inputRef={inputRef}
      onFocus={(e) => {
        handleFocus(fieldId, e.target)
        onFocus?.(e)
      }}
      onBlur={handleOnblurWithValidation}
      onMouseEnter={(e) => handleFocus(fieldId, e.currentTarget)}
      onMouseLeave={() => handleBlur()}
      fullWidth
      sx={styles}
      inputProps={{ inputMode: 'tel' }}
      InputProps={{
        endAdornment: (
          <>
            {showAdornment && (
              <InputAdornment position="end">
                {isError ? (
                  <ErrorOutline
                    color="error"
                    sx={{ fontSize: '24px !important', marginRight: '8px' }}
                  />
                ) : isSuccess ? (
                  <Check
                    color="success"
                    sx={{ fontSize: '24px !important', marginRight: '8px' }}
                  />
                ) : null}
              </InputAdornment>
            )}
          </>
        ),
        inputMode: 'tel',
      }}
      // {...rest}
    />
  )
}

MuiPhoneNumberField.propTypes = {
  id: PropTypes.string,
  value: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  required: PropTypes.bool,
  defaultErrorState: PropTypes.bool,
  forceError: PropTypes.bool,
}

export default MuiPhoneNumberField
